import {
  Card,
  CardHeader,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Divider,
  Paper,
  Alert,
} from '@mui/material'
import { ExpandMore, Close } from '@mui/icons-material'
import { useAdventureEditorStore } from '../../../stores/adventureEditorStore'
import { useCollaborativeAdventureEditor } from '@hooks/useCollaborativeAdventureEditor'
import { usePublishedPermissions } from '../../../hooks/usePublishedPermissions'
import '../styles/TagsPanel.css'

export function TagsPanel() {
  const availableCategories = useAdventureEditorStore((state) => state.availableCategories)
  const adventureTypeCategories = useAdventureEditorStore(
    (state) => state.adventureTypeCategories
  )
  const isLoadingTags = useAdventureEditorStore((state) => state.isLoadingTags)
  const adventure = useAdventureEditorStore((state) => state.adventure)

  // Published permissions - only admins/moderators can edit tags on published adventures
  const { isPublished, canEditTags } = usePublishedPermissions()

  // Collaborative editing hook - syncs tag changes with other editors
  const { toggleTag, removeTag } = useCollaborativeAdventureEditor()

  const selectedTags = adventure?.tags || []

  const handleToggleTag = (tagId: string, selectionMode: 'single' | 'multiple', categoryId: string) => {
    // Block editing when read-only
    if (!canEditTags) return
    toggleTag(tagId, selectionMode, categoryId)
  }

  const handleRemoveTag = (tagId: string) => {
    // Block editing when read-only
    if (!canEditTags) return
    removeTag(tagId)
  }

  // Find tag and category by tag ID
  const findTagAndCategory = (tagId: string) => {
    for (const category of availableCategories) {
      const tag = category.tags.find((t) => t.id === tagId)
      if (tag) {
        return { tag, category }
      }
    }
    return null
  }

  const getTagChipStyle = (categoryColor?: string, isSelected?: boolean) => {
    if (isSelected) {
      return {
        backgroundColor: categoryColor || '#1976d2',
        color: 'white',
        borderColor: categoryColor || '#1976d2',
      }
    }
    return {
      borderColor: categoryColor || '#ccc',
      color: categoryColor || '#666',
    }
  }

  // Check if a category has at least one selected tag
  const categoryHasSelection = (categoryId: string) => {
    const category = availableCategories.find((c) => c.id === categoryId)
    if (!category) return false
    return category.tags.some((tag) => selectedTags.includes(tag.id))
  }

  // Get missing required categories
  const getMissingRequiredCategories = () => {
    return availableCategories.filter(
      (category) => category.selectionRequired && category.isActive && !categoryHasSelection(category.id)
    )
  }

  const missingRequired = getMissingRequiredCategories()

  if (isLoadingTags) {
    return (
      <Card>
        <CardHeader title="Adventure Tags" />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading tag categories...</Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader title="Basic Adventure Tags" />
      <CardContent>
        {/* Published Read-Only Warning */}
        {isPublished && !canEditTags && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Tags are read-only because this adventure is published. Only system moderators can modify tags.
          </Alert>
        )}

        {/* Show validation error if missing required categories */}
        {missingRequired.length > 0 && (
          <Box
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: '#856404', fontWeight: 600 }}>
              ⚠️ Required categories not selected:
            </Typography>
            <Typography variant="body2" sx={{ color: '#856404', mt: 1 }}>
              {missingRequired.map((cat) => cat.displayName).join(', ')}
            </Typography>
          </Box>
        )}
        {/* Basic Categories */}
        {availableCategories
          .filter((c) => c.isActive && c.tags.some((t) => t.isActive))
          .map((category) => {
            const isMissing = category.selectionRequired && !categoryHasSelection(category.id)
            return (
              <Accordion
                key={category.id}
                sx={{
                  border: isMissing ? '2px solid #ffc107' : undefined,
                  backgroundColor: isMissing ? '#fffbf0' : undefined,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box className="category-header">
                    <Box className="category-header-left">
                      <span className="category-icon">{category.icon || '📁'}</span>
                      <span style={{ color: category.color, fontWeight: 500 }}>
                        {category.displayName}
                        {category.selectionMode === 'single' && (
                          <small className="badge badge-single">Single choice</small>
                        )}
                        {category.selectionRequired && (
                          <small className="badge badge-required">Required</small>
                        )}
                      </span>
                    </Box>
                    <Box className="category-header-right">
                      <span
                        className="tag-count-badge"
                        style={{
                          backgroundColor: category.color,
                          color: 'white',
                        }}
                      >
                        {category.tags.filter((t) => t.isActive).length}
                      </span>
                    </Box>
                  </Box>
                </AccordionSummary>
              <AccordionDetails>
                {category.description && (
                  <Typography variant="caption" sx={{ mb: 2, display: 'block', color: '#666' }}>
                    {category.description}
                  </Typography>
                )}
                <Box className="tag-chips-container">
                  {category.tags
                    .filter((t) => t.isActive)
                    .sort((a, b) => {
                      const orderDiff = a.sortOrder - b.sortOrder
                      return orderDiff !== 0 ? orderDiff : a.displayName.localeCompare(b.displayName)
                    })
                    .map((tag) => {
                      const isSelected = selectedTags.includes(tag.id)
                      return (
                        <Chip
                          key={tag.id}
                          label={
                            <>
                              {tag.icon && <span className="tag-icon">{tag.icon}</span>}
                              <span className="tag-label">{tag.displayName}</span>
                            </>
                          }
                          onClick={() =>
                            handleToggleTag(tag.id, category.selectionMode, category.id)
                          }
                          variant={isSelected ? 'filled' : 'outlined'}
                          size="small"
                          sx={getTagChipStyle(category.color, isSelected)}
                          title={tag.description}
                          className="tag-chip"
                        />
                      )
                    })}
                </Box>
              </AccordionDetails>
            </Accordion>
            )
          })}

        {/* Adventure Type Categories */}
        {adventureTypeCategories && adventureTypeCategories.length > 0 && (
          <>
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" sx={{ mb: 3 }}>
              Adventure Type Tags
            </Typography>
            {adventureTypeCategories
              .filter((c) => c.isActive && c.tags.some((t) => t.isActive))
              .map((category) => (
                <Accordion key={category.id}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box className="category-header">
                      <Box className="category-header-left">
                        <span className="category-icon">{category.icon || '📁'}</span>
                        <span style={{ color: category.color, fontWeight: 500 }}>
                          {category.displayName}
                          {category.selectionMode === 'single' && (
                            <small className="badge badge-single">Single choice</small>
                          )}
                        </span>
                      </Box>
                      <Box className="category-header-right">
                        <span
                          className="tag-count-badge"
                          style={{
                            backgroundColor: category.color,
                            color: 'white',
                          }}
                        >
                          {category.tags.filter((t) => t.isActive).length}
                        </span>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {category.description && (
                      <Typography variant="caption" sx={{ mb: 2, display: 'block', color: '#666' }}>
                        {category.description}
                      </Typography>
                    )}
                    <Box className="tag-chips-container">
                      {category.tags
                        .filter((t) => t.isActive)
                        .sort((a, b) => {
                          const orderDiff = a.sortOrder - b.sortOrder
                          return orderDiff !== 0
                            ? orderDiff
                            : a.displayName.localeCompare(b.displayName)
                        })
                        .map((tag) => {
                          const isSelected = selectedTags.includes(tag.id)
                          return (
                            <Chip
                              key={tag.id}
                              label={
                                <>
                                  {tag.icon && <span className="tag-icon">{tag.icon}</span>}
                                  <span className="tag-label">{tag.displayName}</span>
                                </>
                              }
                              onClick={() =>
                                handleToggleTag(tag.id, category.selectionMode, category.id)
                              }
                              variant={isSelected ? 'filled' : 'outlined'}
                              size="small"
                              sx={getTagChipStyle(category.color, isSelected)}
                              title={tag.description}
                              className="tag-chip"
                            />
                          )
                        })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
          </>
        )}

        {/* Selected Tags Section */}
        {selectedTags.length > 0 && (
          <>
            <Divider sx={{ my: 4 }} />
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
              <Typography variant="h6" gutterBottom>
                Selected Tags
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedTags.map((tagId) => {
                  const result = findTagAndCategory(tagId)
                  if (!result) return null

                  const { tag, category } = result
                  return (
                    <Chip
                      key={tagId}
                      label={`${tag.displayName} (${category.displayName})`}
                      onDelete={() => handleRemoveTag(tagId)}
                      deleteIcon={<Close />}
                      sx={{
                        backgroundColor: '#2196f3',
                        color: 'white',
                        '& .MuiChip-deleteIcon': {
                          color: 'white',
                          '&:hover': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          },
                        },
                      }}
                    />
                  )
                })}
              </Box>
            </Paper>
          </>
        )}
      </CardContent>
    </Card>
  )
}
